type AssistantArtifactDownloadGoogleWorkspace = {
  fileName?: string | null;
  mimeType?: string | null;
  artifactDownloadUrl?: string | null;
  workbookArtifactPath?: string | null;
};

export type AssistantArtifactDownloadDescriptor = {
  id?: string | null;
  label?: string | null;
  kind?: string | null;
  badge?: string | null;
  actionLabel?: string | null;
  meta?: string | null;
  href?: string | null;
  sourceRef?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  downloadUrl?: string | null;
  artifactDownloadUrl?: string | null;
  workbookArtifactPath?: string | null;
  format?: string | null;
  googleWorkspace?: AssistantArtifactDownloadGoogleWorkspace | null;
};

export type AssistantSpreadsheetFallbackFileExtension = 'csv' | 'xls' | 'xlsx';

const safeFileStem = (value: unknown): string => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]{1,8}$/i, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'assistant-artifact';
};

const formatSignature = (item: AssistantArtifactDownloadDescriptor): string => [
  item.format,
  item.kind,
  item.badge,
  item.actionLabel,
  item.label,
  item.fileName,
  item.meta,
  item.sourceRef,
  item.href,
  item.downloadUrl,
  item.artifactDownloadUrl,
  item.workbookArtifactPath,
  item.mimeType,
  item.googleWorkspace?.fileName,
  item.googleWorkspace?.mimeType,
  item.googleWorkspace?.artifactDownloadUrl,
  item.googleWorkspace?.workbookArtifactPath,
].filter(Boolean).join(' ');

const resolveArtifactFormatFromSignature = (signature: string): string | null => {
  if (/\.csv\b|text\/csv|\bcsv\b/i.test(signature)) {
    return 'CSV';
  }
  if (/\.json\b|application\/json|\bjson\b/i.test(signature)) {
    return 'JSON';
  }
  if (/\.xlsx?\b|spreadsheet|workbook|excel|\bxls\b/i.test(signature)) {
    return 'Excel';
  }
  if (/\.pdf\b|application\/pdf|\bpdf\b/i.test(signature)) {
    return 'PDF';
  }
  if (/\.md\b|\.markdown\b|text\/markdown|\bmarkdown\b/i.test(signature)) {
    return 'Markdown';
  }
  return null;
};

export const resolveAssistantArtifactFormat = (
  item: AssistantArtifactDownloadDescriptor,
): string => {
  const explicitSignature = `${item.format ?? ''} ${item.kind ?? ''} ${item.badge ?? ''} ${item.actionLabel ?? ''} ${item.label ?? ''}`;
  const explicitFormat = resolveArtifactFormatFromSignature(explicitSignature);
  if (explicitFormat) return explicitFormat;
  const sourceFormat = resolveArtifactFormatFromSignature(formatSignature(item));
  if (sourceFormat) return sourceFormat;
  const label = String(item.kind ?? item.format ?? item.label ?? 'file').trim() || 'file';
  return label
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const extensionCandidatesForArtifact = (
  item: AssistantArtifactDownloadDescriptor,
  format: string,
): { candidates: string[]; fallback: string } => {
  switch (format.toLowerCase()) {
    case 'json':
      return { candidates: ['json'], fallback: 'json' };
    case 'csv':
      return { candidates: ['csv'], fallback: 'csv' };
    case 'excel': {
      const signature = formatSignature(item);
      const fallback = /\.xls\b|application\/vnd\.ms-excel|\bxls\b/i.test(signature) ? 'xls' : 'xlsx';
      return { candidates: fallback === 'xls' ? ['xls', 'xlsx'] : ['xlsx', 'xls'], fallback };
    }
    case 'pdf':
      return { candidates: ['pdf'], fallback: 'pdf' };
    case 'markdown':
      return { candidates: ['md', 'markdown'], fallback: 'md' };
    default:
      return { candidates: ['txt'], fallback: 'txt' };
  }
};

export const resolveAssistantSpreadsheetFallbackFileExtension = (
  item: AssistantArtifactDownloadDescriptor,
): AssistantSpreadsheetFallbackFileExtension => {
  const format = resolveAssistantArtifactFormat(item);
  const { fallback } = extensionCandidatesForArtifact(item, format);
  if (fallback === 'csv' || fallback === 'xls' || fallback === 'xlsx') {
    return fallback;
  }
  return 'xlsx';
};

const resolveArtifactCandidateFileName = (
  value: unknown,
  extension: string,
): string | null => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return null;
  const escapedExtension = extension.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const matchingFileName = text.match(new RegExp(`[A-Za-z0-9._-]+\\.${escapedExtension}\\b`, 'i'))?.[0];
  if (matchingFileName) {
    return matchingFileName;
  }
  const fileName = text.split(/[/?#]/).filter(Boolean).pop() ?? '';
  return new RegExp(`\\.${escapedExtension}$`, 'i').test(fileName) ? fileName : null;
};

export const resolveAssistantArtifactDownloadFileName = (
  item: AssistantArtifactDownloadDescriptor,
): string => {
  const format = resolveAssistantArtifactFormat(item);
  const { candidates, fallback } = extensionCandidatesForArtifact(item, format);
  const candidateSources = [
    item.fileName,
    item.googleWorkspace?.fileName,
    item.meta,
    item.sourceRef,
    item.downloadUrl,
    item.artifactDownloadUrl,
    item.workbookArtifactPath,
    item.href,
    item.googleWorkspace?.artifactDownloadUrl,
    item.googleWorkspace?.workbookArtifactPath,
  ];
  for (const extension of candidates) {
    for (const source of candidateSources) {
      const fileName = resolveArtifactCandidateFileName(source, extension);
      if (fileName) {
        return fileName;
      }
    }
  }
  const stem = safeFileStem(item.fileName ?? item.meta ?? item.label ?? item.kind ?? item.id);
  return stem.endsWith(`.${fallback}`) ? stem : `${stem}.${fallback}`;
};
